import { DestroyRef, inject, Injectable } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { take } from 'rxjs';
import { ApiErrorResponse } from '../../../../core/classes/ApiErrorResponse';
import { NotificationService } from '../../../../core/services/NotificationService';
import { ConfirmationModalComponent } from '../../../../shared/components/confirmation-modal/confirmation-modal.component';
import { WorkerService } from './WorkerService';

interface PrintEcReceiptOptions {
  silent?: boolean;
  showSuccessToast?: boolean;
}

interface PromptAndPrintOptions {
  showSuccessToast?: boolean;
  afterAnswered?: () => void;
}

/**
 * Orchestrates printing of the buffered EC slip on the POS/Epson printer, mirroring legacy
 * CXCash CDlgCXCash::PrintEC: prompt whether to print the customer copy, then forward the
 * buffered slip. The merchant copy is always printed by the worker.
 */
@Injectable({
  providedIn: 'root'
})
export class EcReceiptPrintService {

  private readonly _workerService = inject(WorkerService);
  private readonly _notificationService = inject(NotificationService);

  /**
   * Prompts for the customer copy, then prints. `afterAnswered` runs once the cashier answers,
   * regardless of the print result (the EC charge is independent of the paper receipt).
   */
  public promptAndPrint(destroyRef: DestroyRef, options: PromptAndPrintOptions = {}): void {
    const onCancelSub = ConfirmationModalComponent.instance.onCancel
      .pipe(
        take(1),
        takeUntilDestroyed(destroyRef)
      )
      .subscribe(() => {
        this.print(false, { showSuccessToast: options.showSuccessToast });
        options.afterAnswered?.();
      });

    ConfirmationModalComponent.openConfirmationModal(
      'EC Receipt'.trans('worker/ec-receipt/customer-copy-confirm.Title'),
      'Do you want to print the customer receipt?'.trans('worker/ec-receipt/customer-copy-confirm.Text'),
      destroyRef,
      () => {
        onCancelSub.unsubscribe();
        this.print(true, { showSuccessToast: options.showSuccessToast });
        options.afterAnswered?.();
      }
    );
  }

  public print(printCustomerCopy: boolean, options: PrintEcReceiptOptions = {}): void {
    this._workerService.printEcBufferedReceipt(printCustomerCopy).subscribe({
      next: () => {
        if (options.showSuccessToast) {
          this._notificationService.pushSuccess(
            'Erfolg'.trans('notifications/success.Title'),
            'Receipt printed successfully.'.trans('worker/ec-receipt/notifications/print-success.Message')
          );
        }
      },
      error: ({ error }: ApiErrorResponse) => {
        if (options.silent) {
          return;
        }

        this._notificationService.pushError(
          'Fehler'.trans('notifications/error.Title'),
          error.message.trans(`print-ec-buffered-receipt/notifications/${error.message}.Message`)
        );
      }
    });
  }
}
